/**
 * Composition method for gallery modules
 * 
 * Author: Sahil David
 */

import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { getLyraEndpoints } from '@/core/modules/lyra/services';
import { useStore } from 'vuex';

/**
 * Composition method to return gallery overall data
 */
export function galleryComposition () {
	const router = useRouter();
	const route = useRoute();

	// Computed router param
	const sectionId = computed((): string => route.params.sectionId as string || '');

	// Function to check if the section is active
	function isActiveSection (sectionIdInput: string): boolean {
		return sectionId.value === sectionIdInput;
	}

	// Activate gallery section 
	function makeSectionActive (section: any): void {
		if (section.pdf) {
			window.open(`/pdf/${section.pdf}.pdf`);
		} else {
			router.push({
				params: { sectionId: section.endpointId }
			})
		}
	}

	return { sectionId, isActiveSection, makeSectionActive }
}

/**
 * Composition method to return gallery content
 */
export function galleryContentComposition () {
	const route = useRoute();
	const store = useStore();

	// Data members
	const activeView = ref<string>('grid');
	const hiddenSections = ref<string[]>([]);

	// Computed router param
	const sectionId = computed((): string => route.params.sectionId as string || '');

	// Method to derive gallery data in computer prop
	const galleryData = computed<any[]>((): any[] => {
		const datum: any[] = [];
		if (!sectionId.value) { return datum; }

		// Get active session
		const sections = getLyraEndpoints('sections');
		const currentSection = sections.find((_: any) => _.endpointId === sectionId.value);
		if (!currentSection) { return datum; }
		
		// Get endpoints
		const endpointSections = getLyraEndpoints(currentSection.sections);
		let endpoints = getLyraEndpoints(currentSection.endpoints);
		endpoints = endpoints.filter((_: any) => _['navigation-display'] === 'true');

		// Push endpoints with no sections
		const noSectionEndpoints = endpoints.filter((_: any) => !_[currentSection.sections]);
		if (noSectionEndpoints && noSectionEndpoints.length > 0) {
			datum.push({
				hasSection: false,
				records: noSectionEndpoints
			});
		}

		// Section endpoints
		if (endpointSections && endpointSections.length > 0) {
			endpointSections.forEach((obj: any) => {
				let sectionEndpoints = endpoints.filter((_: any) => _[currentSection.sections] === obj.endpointId);
				sectionEndpoints = sectionEndpoints.sort((a: any, b: any) => parseFloat(a.order) - parseFloat(b.order));

				datum.push({
					hasSection: true,
					section: obj.endpointId,
					records: sectionEndpoints,
				});
			});
		}
		
		return datum;
	});

	// Function to toggle reactive slides
	const showReactive = computed((): boolean => store.getters.get_reactiveState);

	function toggleReactiveSlides () {
		store.dispatch('axn_toggleReactiveDisplay');
	}

	// Function to check if the view is grid
	function makeViewActive (view: string): void {
		activeView.value = view;
	}

	// Function to toggle section hidden view
	function toggleSectionHiddenView (section: string): void {
		if (hiddenSections.value.includes(section)) {
			const indexOf = hiddenSections.value.indexOf(section);
			hiddenSections.value.splice(indexOf, 1);
		} else {
			hiddenSections.value.push(section);
		}
	}

	return { 
		sectionId, galleryData, activeView, hiddenSections, 
		makeViewActive, toggleSectionHiddenView, toggleReactiveSlides,
		showReactive
	}
}